import { useMemberCardIdentity } from '@/hooks/member/useMemberCardIdentity';
import type { PracticeCardContact } from '@/hooks/practice/practiceApi';
import { usePracticeInfo } from '@/hooks/practice/usePracticeInfo';

/** Everything printed on either face of the membership card, from both source records. */
export interface MembershipCardData {
  memberName: string | null;
  mdvipId: string | null;
  cardDisplayName: string | null;
  coBrandName: string | null;
  cardContacts: PracticeCardContact[];
  additionalLine1: string | null;
  additionalLine2: string | null;
  membershipTier: string | null;
}

/**
 * The membership card's full content, composed from the practice record (the card's display name,
 * co-brand, phone lines and tier) and the member's own identity. Both halves read shared query
 * keys, so this adds no request of its own.
 *
 * Pending until both halves resolve; `isError`/`error` report the practice failure first, since
 * that's the one with a distinct "no assigned physician" meaning the caller branches on.
 */
export function useMembershipCard() {
  const practice = usePracticeInfo();
  const identity = useMemberCardIdentity();

  const isPending = practice.isPending || identity.isPending;
  const isError = practice.isError || identity.isError;
  const error = practice.error ?? identity.error;

  const data: MembershipCardData | undefined =
    practice.data && identity.data
      ? {
          memberName: [identity.data.firstName, identity.data.lastName].filter(Boolean).join(' ') || null,
          mdvipId: identity.data.mdvipId,
          cardDisplayName: practice.data.cardDisplayName,
          coBrandName: practice.data.coBrandName,
          cardContacts: practice.data.cardContacts,
          additionalLine1: practice.data.additionalLine1,
          additionalLine2: practice.data.additionalLine2,
          membershipTier: practice.data.membershipTier,
        }
      : undefined;

  return { data, isPending, isError, error };
}
